import React, { useState, useRef, useEffect } from 'react';
import './MIDownloadButtonUI.css';
import RestApiUtils from '../../../apiclient/moebli/RestApiUtils';

const MIDownloadButtonUI = ({
  yearMonth,
  context,
  apiBase = '/api/su/monitoringIntelligence'
}) => {
  const [open, setOpen] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingXls, setLoadingXls] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = function (e) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);

    return function () {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  const download = (format) => {
    const setLoading = format === 'pdf' ? setLoadingPdf : setLoadingXls;

    setLoading(true);
    setOpen(false);
    setErrorMsg(null);

    const endpoint =
      format === 'pdf'
        ? apiBase + '/miReportDownloadPdf'
        : apiBase + '/miReportDownloadExcel';

    const body = {
      year:
        yearMonth && yearMonth.year
          ? yearMonth.year
          : new Date().getFullYear(),
      month:
        yearMonth && yearMonth.month
          ? yearMonth.month
          : new Date().getMonth() + 1
    };

    RestApiUtils.post(
      context,
      endpoint,
      (data) => {
        setLoading(false);

        if (data && data.signedUrl && data.signedUrl.signedUrl) {
          // ✅ Use anchor click — same pattern as onDocDownloadClickCallback
          const url = data.signedUrl.signedUrl;
          const link = document.createElement('a');
          link.href = url;
          link.download = data.signedUrl.fileName || '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          let errMsg = 'Download failed.';
          if (
            data &&
            data.errorList &&
            data.errorList.length > 0 &&
            data.errorList[0] &&
            data.errorList[0].message
          ) {
            errMsg = data.errorList[0].message;
          }
          setErrorMsg(errMsg);
        }
      },
      body
    );
  };

  const isLoading = loadingPdf || loadingXls;

  return (
    <div className="mi-dl-wrapper" ref={dropdownRef}>
      <button
        className={
          'mi-dl-btn' +
          (isLoading ? ' mi-dl-btn--loading' : '')
        }
        onClick={() =>
          !isLoading && setOpen(function (v) { return !v; })
        }
        title="Download report"
        aria-haspopup="true"
        aria-expanded={open}
        disabled={isLoading}
      >
        {isLoading ? (
          <span
            className="mi-dl-spinner"
            aria-label="Loading"
          />
        ) : (
          <svg
            className="mi-dl-icon"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M10 3v10m0 0l-3-3m3 3l3-3"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 15h12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        )}

        <span className="mi-dl-label">
          {loadingPdf
            ? 'Generating PDF...'
            : loadingXls
              ? 'Generating Excel...'
              : 'Download'}
        </span>

        {!isLoading && (
          <svg
            className={
              'mi-dl-chevron' +
              (open ? ' mi-dl-chevron--open' : '')
            }
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {open && (
        <div className="mi-dl-dropdown" role="menu">
          <button
            className="mi-dl-option"
            role="menuitem"
            onClick={() => download('pdf')}
          >
            <span className="mi-dl-option-icon mi-dl-option-icon--pdf">
              PDF
            </span>
            <span>
              <span className="mi-dl-option-title">
                PDF
              </span>
              <span className="mi-dl-option-sub">
                Formatted report, printable
              </span>
            </span>
          </button>

          <div className="mi-dl-divider" />

          <button
            className="mi-dl-option"
            role="menuitem"
            onClick={() => download('excel')}
          >
            <span className="mi-dl-option-icon mi-dl-option-icon--xls">
              XLS
            </span>
            <span>
              <span className="mi-dl-option-title">
                Excel
              </span>
              <span className="mi-dl-option-sub">
                Spreadsheet, editable
              </span>
            </span>
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="mi-dl-error" role="alert">
          <span>{errorMsg}</span>

          <button
            className="mi-dl-error-close"
            onClick={() => setErrorMsg(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default MIDownloadButtonUI;